import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import PharmeasyLayout from "@/components/PharmeasyLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { 
  Truck, RefreshCw, Search, Phone, User, 
  Package, IndianRupee, Clock, CheckCircle2,
  TrendingUp, ArrowRight, X, MapPin, ExternalLink, Route, ArrowUpDown,
  Navigation, List, Map as MapIcon, Home, Copy, TrafficCone, ChevronDown, History, AlertCircle, Loader2
} from "lucide-react";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;

const PharmeasyTrips = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState("active");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Trip detail modal
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [tripShipments, setTripShipments] = useState([]);
  const [loadingShipments, setLoadingShipments] = useState(false);
  const [syncingTasks, setSyncingTasks] = useState(null);
  const [optimizingRoute, setOptimizingRoute] = useState(false);
  const [sortByEdgSequence, setSortByEdgSequence] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'map'
  const [hubLocation, setHubLocation] = useState(null);
  const [useLiveTraffic, setUseLiveTraffic] = useState(false);
  const [showOptimizeOptions, setShowOptimizeOptions] = useState(false);

  useEffect(() => {
    fetchTrips();
    fetchStats();
  }, [statusFilter, page]);

  const fetchTrips = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        size: "20",
      });
      // Only add status_filter if it's not "all"
      if (statusFilter && statusFilter !== "all") {
        params.append("status_filter", statusFilter);
      }
      if (searchQuery) params.append("search", searchQuery);
      
      const res = await axios.get(`${API}/pharmeasy-orders/trips?${params}`);
      setTrips(res.data.data || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.total_pages || 1);
    } catch (error) {
      console.error("Failed to fetch trips:", error);
      toast.error("Failed to load trips");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/pharmeasy-orders/trips/stats`);
      setStats(res.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  };

  const syncTripTasks = async (tripId) => {
    setSyncingTasks(tripId);
    try {
      const res = await axios.post(`${API}/pharmeasy-orders/trips/${tripId}/sync-tasks`);
      toast.success(`Synced ${res.data.total_tasks} tasks (${res.data.new_shipments} new shipments)`);
      
      // Refresh shipments if modal is open
      if (selectedTrip?.tripId === tripId) {
        fetchTripShipments(tripId);
      }
      fetchTrips();
    } catch (error) {
      console.error("Task sync failed:", error);
      toast.error(error.response?.data?.detail?.error || "Task sync failed");
    } finally {
      setSyncingTasks(null);
    }
  };

  const openTripDetail = async (trip) => {
    setSelectedTrip(trip);
    setSortByEdgSequence(false);
    setViewMode('list');
    fetchTripShipments(trip.tripId);
    fetchHubLocation(trip.tripId);
  };

  const fetchTripShipments = async (tripId) => {
    setLoadingShipments(true);
    try {
      const res = await axios.get(`${API}/pharmeasy-orders/trips/${tripId}/shipments`);
      setTripShipments(res.data.data || []);
    } catch (error) {
      console.error("Failed to fetch shipments:", error);
      setTripShipments([]);
    } finally {
      setLoadingShipments(false);
    }
  };

  const fetchHubLocation = async (tripId) => {
    try {
      // Fetch hub details for this trip
      const res = await axios.get(`${API}/pharmeasy-orders/trips/${tripId}/hub-location`);
      if (res.data && res.data.coordinates) {
        setHubLocation(res.data);
      }
    } catch (error) {
      console.error("Failed to fetch hub location:", error);
      // Default hub location (EDG1_HYD_KPBH)
      setHubLocation({
        name: "EDG1_HYD_KPBH",
        coordinates: { lat: 17.4401, lng: 78.3489 }
      });
    }
  };

  const optimizeRoute = async (tripId) => {
    setOptimizingRoute(true);
    setShowOptimizeOptions(false);
    try {
      const res = await axios.post(`${API}/pharmeasy-orders/trips/${tripId}/optimize-route`, {
        use_live_traffic: useLiveTraffic
      });
      const { details_fetched, geocoded_count, optimized_count, no_coordinates_count, used_live_traffic } = res.data;
      
      let message = `Route optimized! ${optimized_count} shipments sequenced.`;
      if (used_live_traffic) {
        message += ' (with live traffic)';
      }
      if (details_fetched > 0 || geocoded_count > 0) {
        message += ` Fetched: ${details_fetched}, Geocoded: ${geocoded_count}.`;
      }
      if (no_coordinates_count > 0) {
        message += ` ${no_coordinates_count} without coords placed at end.`;
      }
      toast.success(message);
      
      // Refresh shipments to get updated sequenceByEdg
      await fetchTripShipments(tripId);
      setSortByEdgSequence(true);
    } catch (error) {
      console.error("Route optimization failed:", error);
      const errorMsg = error.response?.data?.detail || "Route optimization failed";
      toast.error(errorMsg);
    } finally {
      setOptimizingRoute(false);
    }
  };

  // Get sorted shipments based on current sort mode
  const getSortedShipments = () => {
    if (!tripShipments.length) return [];
    
    if (sortByEdgSequence) {
      // Sort by sequenceByEdg if available, otherwise put at end
      return [...tripShipments].sort((a, b) => {
        const seqA = a.sequenceByEdg ?? 999999;
        const seqB = b.sequenceByEdg ?? 999999;
        return seqA - seqB;
      });
    }
    
    // Default sort by priority
    return [...tripShipments].sort((a, b) => {
      const prioA = a.priority ?? 999999;
      const prioB = b.priority ?? 999999;
      return prioA - prioB;
    });
  };

  // Check if any shipment has sequenceByEdg
  const hasEdgSequence = tripShipments.some(s => s.sequenceByEdg != null);

  // Copy to clipboard function
  const copyToClipboard = (text, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied!`);
  };

  // Get map link for a shipment
  // Get the relevant address based on shipment type
  // For RETURN: srcAddress is the pickup location (customer)
  // For FORWARD/DELIVERY: destAddress is the delivery location (customer)
  const getRelevantAddress = (shipment) => {
    if (shipment?.type === "RETURN") {
      return shipment.srcAddress;
    }
    return shipment?.destAddress;
  };

  const getMapLink = (shipment) => {
    // For RETURN type, use srcAddress coordinates
    if (shipment?.type === "RETURN") {
      const calcCoords = shipment?.calculatedSrcAddressCoordinate;
      if (calcCoords?.success && calcCoords?.coordinates) {
        return `https://www.google.com/maps?q=${calcCoords.coordinates.lat},${calcCoords.coordinates.lng}`;
      }
      const srcAddr = shipment?.srcAddress;
      if (srcAddr?.latitude && srcAddr?.longitude) {
        const lat = parseFloat(srcAddr.latitude);
        const lng = parseFloat(srcAddr.longitude);
        if (lat !== 0 && lng !== 0 && !isNaN(lat) && !isNaN(lng)) {
          return `https://www.google.com/maps?q=${lat},${lng}`;
        }
      }
    } else {
      // For FORWARD/DELIVERY, use destAddress coordinates
      const calcCoords = shipment?.calculatedDestAddressCoordinate;
      if (calcCoords?.success && calcCoords?.coordinates) {
        return `https://www.google.com/maps?q=${calcCoords.coordinates.lat},${calcCoords.coordinates.lng}`;
      }
      const destAddr = shipment?.destAddress;
      if (destAddr?.latitude && destAddr?.longitude) {
        const lat = parseFloat(destAddr.latitude);
        const lng = parseFloat(destAddr.longitude);
        if (lat !== 0 && lng !== 0 && !isNaN(lat) && !isNaN(lng)) {
          return `https://www.google.com/maps?q=${lat},${lng}`;
        }
      }
    }
    return null;
  };

  // Format address from address object
  const formatAddress = (addr) => {
    if (!addr) return "—";
    const parts = [addr.address1, addr.city, addr.pincode].filter(Boolean);
    return parts.join(", ") || "—";
  };

  // Get coordinates from shipment (considers type for RETURN)
  const getCoordinates = (shipment) => {
    if (shipment?.type === "RETURN") {
      const calcCoords = shipment?.calculatedSrcAddressCoordinate;
      if (calcCoords?.success && calcCoords?.coordinates) {
        return calcCoords.coordinates;
      }
      const srcAddr = shipment?.srcAddress;
      if (srcAddr?.latitude && srcAddr?.longitude) {
        const lat = parseFloat(srcAddr.latitude);
        const lng = parseFloat(srcAddr.longitude);
        if (lat !== 0 && lng !== 0 && !isNaN(lat) && !isNaN(lng)) {
          return { lat, lng };
        }
      }
    } else {
      const calcCoords = shipment?.calculatedDestAddressCoordinate;
      if (calcCoords?.success && calcCoords?.coordinates) {
        return calcCoords.coordinates;
      }
      const destAddr = shipment?.destAddress;
      if (destAddr?.latitude && destAddr?.longitude) {
        const lat = parseFloat(destAddr.latitude);
        const lng = parseFloat(destAddr.longitude);
        if (lat !== 0 && lng !== 0 && !isNaN(lat) && !isNaN(lng)) {
          return { lat, lng };
        }
      }
    }
    return null;
  };

  // Build Google Maps Embed URL with directions
  const buildDirectionsEmbedUrl = (shipments, origin) => {
    const sortedShipments = sortByEdgSequence 
      ? [...shipments].sort((a, b) => (a.sequenceByEdg ?? 999) - (b.sequenceByEdg ?? 999))
      : shipments;
    
    const waypoints = sortedShipments
      .map(s => getCoordinates(s))
      .filter(Boolean)
      .map(c => `${c.lat},${c.lng}`);
    
    if (waypoints.length === 0) return null;
    
    // Use hub as origin if available
    const startPoint = origin || waypoints[0];
    const originStr = typeof startPoint === 'string' ? startPoint : `${startPoint.lat},${startPoint.lng}`;
    
    const destination = waypoints[waypoints.length - 1];
    const waypointsStr = waypoints.slice(0, -1).join('|');
    
    let url = `https://www.google.com/maps/embed/v1/directions?key=${GOOGLE_MAPS_API_KEY}&origin=${originStr}&destination=${destination}`;
    if (waypointsStr) {
      url += `&waypoints=${waypointsStr}`;
    }
    url += `&mode=driving`;
    
    return url;
  };

  // Build full Google Maps directions URL
  const buildFullMapUrl = (shipments, origin) => {
    const sortedShipments = sortByEdgSequence 
      ? [...shipments].sort((a, b) => (a.sequenceByEdg ?? 999) - (b.sequenceByEdg ?? 999))
      : shipments;
    
    const waypoints = sortedShipments
      .map(s => getCoordinates(s))
      .filter(Boolean)
      .map(c => `${c.lat},${c.lng}`);
    
    if (waypoints.length === 0) return null;
    
    // Start with hub location
    let allPoints = [];
    if (origin) {
      allPoints.push(`${origin.lat},${origin.lng}`);
    }
    allPoints = [...allPoints, ...waypoints];
    
    return `https://www.google.com/maps/dir/${allPoints.join('/')}`;
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchTrips();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "COMPLETED":
        return "bg-green-100 text-green-700";
      case "OUT_FOR_DELIVERY":
        return "bg-blue-100 text-blue-700";
      case "RECON_STARTED":
      case "RECON_FINISHED":
        return "bg-purple-100 text-purple-700";
      case "CREATED":
        return "bg-slate-100 text-slate-700";
      case "POSTPONED":
        return "bg-amber-100 text-amber-700";
      default:
        return "bg-amber-100 text-amber-700";
    }
  };

  const getShipmentStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case "DELIVERED":
        return "bg-green-100 text-green-700";
      case "OUT_FOR_DELIVERY":
        return "bg-blue-100 text-blue-700";
      case "PENDING":
        return "bg-amber-100 text-amber-700";
      case "CANCELLED":
      case "REJECTED":
      case "RTO":
        return "bg-red-100 text-red-700";
      case "ATTEMPTED":
        return "bg-orange-100 text-orange-700";
      default:
        return "bg-slate-100 text-slate-600";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "COMPLETED":
        return <CheckCircle2 className="w-4 h-4" />;
      case "OUT_FOR_DELIVERY":
        return <Truck className="w-4 h-4" />;
      case "RECON_STARTED":
      case "RECON_FINISHED":
        return <TrendingUp className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      // Bolt API returns IST timestamps tagged as +00:00, so display as UTC to avoid double offset
      return new Date(dateStr).toLocaleString('en-IN', {
        day: 'numeric',
        month: 'short',
        timeZone: 'UTC',
      });
    } catch {
      return dateStr;
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    try {
      // Bolt API returns IST timestamps tagged as +00:00, so display as UTC to avoid double offset
      return new Date(dateStr).toLocaleString('en-IN', {
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'UTC',
      });
    } catch {
      return "";
    }
  };

  // Calculate stat summaries (exclude COMPLETED and RECON_FINISHED from active)
  const activeTrips = stats?.by_status ? 
    Object.entries(stats.by_status)
      .filter(([status]) => !["COMPLETED", "RECON_FINISHED"].includes(status))
      .reduce((sum, [, data]) => sum + data.count, 0) : 0;
  
  const reconFinishedTrips = stats?.by_status?.RECON_FINISHED?.count || 0;
  const completedTrips = stats?.by_status?.COMPLETED?.count || 0;
  const totalCashToCollect = stats?.by_status ? 
    Object.values(stats.by_status).reduce((sum, data) => sum + (data.totalCashToCollect || 0), 0) : 0;
  const totalCashCollected = stats?.by_status ? 
    Object.values(stats.by_status).reduce((sum, data) => sum + (data.totalCashCollected || 0), 0) : 0;
  
  // Cash breakdown from stats
  const totalCashInCash = stats?.cash_breakdown?.total_cash_in_cash || 0;
  const totalCashInQR = stats?.cash_breakdown?.total_cash_in_qr || 0;

  // Universal Loader
  if (loading) {
    return (
      <PharmeasyLayout user={user} onLogout={onLogout}>
        <div className="flex flex-col items-center justify-center h-[60vh]">
          <Loader2 className="w-12 h-12 animate-spin text-emerald-500" />
          <p className="text-slate-500 mt-4 text-lg">Loading Trips...</p>
        </div>
      </PharmeasyLayout>
    );
  }

  return (
    <PharmeasyLayout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="pharmeasy-trips">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-800">Trips</h1>
            <p className="text-slate-500">Manage delivery trips and tracking</p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600">Active Trips</p>
                  <p className="text-2xl font-bold text-blue-800">{activeTrips}</p>
                </div>
                <Truck className="w-8 h-8 text-blue-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-600">Recon Finished</p>
                  <p className="text-2xl font-bold text-purple-800">{reconFinishedTrips}</p>
                </div>
                <TrendingUp className="w-8 h-8 text-purple-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-green-50 border-green-200">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600">Completed</p>
                  <p className="text-2xl font-bold text-green-800">{completedTrips}</p>
                </div>
                <CheckCircle2 className="w-8 h-8 text-green-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-amber-600">To Collect</p>
                  <p className="text-2xl font-bold text-amber-800">{formatCurrency(totalCashToCollect)}</p>
                </div>
                <IndianRupee className="w-8 h-8 text-amber-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-green-50 border-green-300">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600">Cash Collected</p>
                  <p className="text-2xl font-bold text-green-700">{formatCurrency(totalCashInCash)}</p>
                </div>
                <IndianRupee className="w-8 h-8 text-green-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-violet-50 border-violet-200">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-violet-600">QR Collected</p>
                  <p className="text-2xl font-bold text-violet-700">{formatCurrency(totalCashInQR)}</p>
                </div>
                <IndianRupee className="w-8 h-8 text-violet-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-emerald-50 border-emerald-200">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-emerald-600">Total Collected</p>
                  <p className="text-2xl font-bold text-emerald-800">{formatCurrency(totalCashInCash + totalCashInQR)}</p>
                </div>
                <CheckCircle2 className="w-8 h-8 text-emerald-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex flex-wrap gap-4 items-end">
              <div className="flex-1 min-w-[200px]">
                <form onSubmit={handleSearch} className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search by Trip ID, Agent name, Phone..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </form>
              </div>
              <div className="w-[180px]">
                <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Trips</SelectItem>
                    <SelectItem value="active">Active Only</SelectItem>
                    <SelectItem value="recon_finished">Recon Finished</SelectItem>
                    <SelectItem value="completed">Completed Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" onClick={handleSearch}>
                <Search className="w-4 h-4 mr-2" />
                Search
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Trips Table */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">
                {statusFilter === "completed" ? "Completed Trips" : 
                 statusFilter === "recon_finished" ? "Recon Finished Trips" :
                 statusFilter === "active" ? "Active Trips" : "All Trips"}
              </CardTitle>
              <Badge variant="outline">{total} trips</Badge>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : trips.length === 0 ? (
              <div className="text-center py-12">
                <Truck className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No trips found</p>
                <p className="text-sm text-slate-400">Try syncing or changing filters</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-slate-50">
                      <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Trip ID</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Agent</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Status</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Tasks</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-emerald-600 whitespace-nowrap">Updated Tasks</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-amber-600 whitespace-nowrap">To Collect</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-green-600 whitespace-nowrap">
                        <span className="block text-[10px] text-slate-400 font-normal">Collected</span>
                        Cash (₹)
                      </th>
                      <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-purple-600 whitespace-nowrap">
                        <span className="block text-[10px] text-slate-400 font-normal">Collected</span>
                        QR (₹)
                      </th>
                      <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-emerald-600 whitespace-nowrap">
                        <span className="block text-[10px] text-slate-400 font-normal">Collected</span>
                        Total (₹)
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Updated</th>
                      <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trips.map((trip) => (
                      <tr key={trip._id} className="border-b hover:bg-slate-50">
                        <td className="px-4 py-3 align-middle">
                          <span className="font-mono font-medium text-slate-800">
                            {trip.tripId}
                          </span>
                          {trip.tasks_fetched && (
                            <Badge variant="outline" className="ml-2 text-xs text-green-600 border-green-200">
                              Synced
                            </Badge>
                          )}
                        </td>
                        <td className="px-4 py-3 align-middle">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                              <User className="w-4 h-4 text-slate-600" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-800">{trip.agentName || "Unassigned"}</p>
                              {trip.phoneNumber && (
                                <p className="text-xs text-slate-500 flex items-center gap-1">
                                  <Phone className="w-3 h-3" />
                                  {trip.phoneNumber}
                                </p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 align-middle">
                          <Badge className={`${getStatusColor(trip.status)} flex items-center gap-1 w-fit`}>
                            {getStatusIcon(trip.status)}
                            {trip.status?.replace("_", " ")}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 align-middle text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Package className="w-4 h-4 text-slate-400" />
                            <span className="font-medium">{trip.taskCount || 0}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 align-middle text-right">
                          <div className="flex items-center justify-end gap-1">
                            {trip.tasks_count_synced != null ? (
                              <>
                                <Package className="w-4 h-4 text-emerald-500" />
                                <span className="font-medium text-emerald-700">{trip.tasks_count_synced}</span>
                              </>
                            ) : (
                              <span className="text-slate-300">—</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 align-middle text-right">
                          <span className={`font-medium ${trip.cashToBeCollected > 0 ? "text-amber-600" : "text-slate-400"}`}>
                            {formatCurrency(trip.cash_breakdown?.total_collectable || trip.cashToBeCollected)}
                          </span>
                        </td>
                        <td className="px-4 py-3 align-middle text-right">
                          <span className={`font-medium ${(trip.cash_breakdown?.cash_in_cash || 0) > 0 ? "text-green-600" : "text-slate-400"}`}>
                            {formatCurrency(trip.cash_breakdown?.cash_in_cash || 0)}
                          </span>
                        </td>
                        <td className="px-4 py-3 align-middle text-right">
                          <span className={`font-medium ${(trip.cash_breakdown?.cash_in_qr || 0) > 0 ? "text-purple-600" : "text-slate-400"}`}>
                            {formatCurrency(trip.cash_breakdown?.cash_in_qr || 0)}
                          </span>
                        </td>
                        <td className="px-4 py-3 align-middle text-right">
                          <span className={`font-medium ${(trip.cash_breakdown?.total_collected || trip.cashCollected) > 0 ? "text-emerald-600" : "text-slate-400"}`}>
                            {formatCurrency(trip.cash_breakdown?.total_collected || trip.cashCollected)}
                          </span>
                        </td>
                        <td className="px-4 py-3 align-middle">
                          <div className="whitespace-nowrap">
                            <div>{formatDate(trip.updatedOn)}</div>
                            <div className="text-xs text-slate-500">{formatTime(trip.updatedOn)}</div>
                          </div>
                        </td>
                        <td className="px-4 py-3 align-middle">
                          <div className="flex items-center justify-center gap-1">
                            {/* Only show Sync button for active trips */}
                            {!["COMPLETED", "RECON_FINISHED"].includes(trip.status) && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => syncTripTasks(trip.tripId)}
                                disabled={syncingTasks === trip.tripId}
                                className="text-emerald-600 border-emerald-200 hover:bg-emerald-50 hover:border-emerald-300 px-2 py-1 h-7 w-7"
                                title="Sync trip tasks"
                              >
                                <RefreshCw className={`w-3 h-3 ${syncingTasks === trip.tripId ? "animate-spin" : ""}`} />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/pharmeasy/trips/${trip.tripId}`)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            >
                              Detail <ArrowRight className="w-3 h-3 ml-1" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <p className="text-sm text-slate-500">
                  Page {page} of {totalPages}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage(p => p - 1)}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => setPage(p => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Trip Detail Modal */}
      <Dialog open={!!selectedTrip} onOpenChange={() => setSelectedTrip(null)}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="pr-8">
            <DialogTitle className="flex items-center justify-between">
              <span>Trip #{selectedTrip?.tripId} - {selectedTrip?.agentName}</span>
              <Badge className={getStatusColor(selectedTrip?.status)}>
                {selectedTrip?.status?.replace("_", " ")}
              </Badge>
            </DialogTitle>
          </DialogHeader>
          
          {selectedTrip && (
            <div className="space-y-4">
              {/* Trip Info */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div className="bg-slate-50 p-4 rounded-lg">
                  <p className="text-xs text-slate-500">Tasks</p>
                  <p className="text-lg font-bold">{selectedTrip.taskCount || 0}</p>
                </div>
                <div className="bg-amber-50 p-4 rounded-lg">
                  <p className="text-xs text-amber-600">To Collect</p>
                  <p className="text-lg font-bold text-amber-700">{formatCurrency(selectedTrip.cashToBeCollected)}</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-xs text-green-600">Collected</p>
                  <p className="text-lg font-bold text-green-700">{formatCurrency(selectedTrip.cashCollected)}</p>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-xs text-blue-600">Phone</p>
                  <p className="text-sm font-bold text-blue-700 truncate">{selectedTrip.phoneNumber || "—"}</p>
                </div>
                <div className="bg-emerald-50 p-4 rounded-lg">
                  <p className="text-xs text-emerald-600 flex items-center gap-1">
                    <Home className="w-3 h-3" /> Pickup (Hub)
                  </p>
                  <p className="text-sm font-bold text-emerald-700 truncate">{hubLocation?.name || "EDG1_HYD_KPBH"}</p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap items-center justify-between gap-2 border-t pt-4">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-slate-800">Shipments ({tripShipments.length})</h3>
                  {/* View Toggle */}
                  <div className="flex items-center gap-0.5 bg-slate-100 rounded-lg p-0.5">
                    <Button
                      variant={viewMode === 'list' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setViewMode('list')}
                      className={`text-xs px-2 h-7 ${viewMode === 'list' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
                    >
                      <List className="w-3.5 h-3.5 mr-1" /> List
                    </Button>
                    <Button
                      variant={viewMode === 'map' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setViewMode('map')}
                      className={`text-xs px-2 h-7 ${viewMode === 'map' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
                    >
                      <MapIcon className="w-3.5 h-3.5 mr-1" /> Map
                    </Button>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {hasEdgSequence && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSortByEdgSequence(!sortByEdgSequence)}
                      className={sortByEdgSequence ? "bg-emerald-50 border-emerald-300 text-emerald-700" : ""}
                    >
                      <ArrowUpDown className="w-4 h-4 mr-1" />
                      {sortByEdgSequence ? "Edg Seq" : "Priority"}
                    </Button>
                  )}
                  {/* Optimize Button with Dropdown */}
                  <div className="relative">
                    <div className="flex">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => optimizeRoute(selectedTrip.tripId)}
                        disabled={optimizingRoute || tripShipments.length === 0}
                        className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-r-none border-r-0"
                      >
                        <Route className={`w-4 h-4 mr-1 ${optimizingRoute ? "animate-pulse" : ""}`} />
                        {optimizingRoute ? "Optimizing..." : "Optimize"}
                        {useLiveTraffic && <TrafficCone className="w-3 h-3 ml-1 text-amber-500" />}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowOptimizeOptions(!showOptimizeOptions)}
                        disabled={optimizingRoute}
                        className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-l-none px-1.5"
                      >
                        <ChevronDown className={`w-4 h-4 transition-transform ${showOptimizeOptions ? 'rotate-180' : ''}`} />
                      </Button>
                    </div>
                    {/* Dropdown Options */}
                    {showOptimizeOptions && (
                      <div className="absolute right-0 top-full mt-1 bg-white border rounded-lg shadow-lg z-50 min-w-[200px]">
                        <div className="p-3 space-y-3">
                          <div className="flex items-center justify-between">
                            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                              <TrafficCone className="w-4 h-4 text-amber-500" />
                              Live Traffic
                            </label>
                            <button
                              onClick={() => setUseLiveTraffic(!useLiveTraffic)}
                              className={`w-10 h-5 rounded-full transition-colors ${useLiveTraffic ? 'bg-emerald-500' : 'bg-slate-300'}`}
                            >
                              <div className={`w-4 h-4 bg-white rounded-full shadow transition-transform ${useLiveTraffic ? 'translate-x-5' : 'translate-x-0.5'}`} />
                            </button>
                          </div>
                          <p className="text-xs text-slate-500">
                            {useLiveTraffic ? 'Route will consider current traffic conditions' : 'Route based on distance only'}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                  {/* Only show Sync button for active trips (not COMPLETED or RECON_FINISHED) */}
                  {!["COMPLETED", "RECON_FINISHED"].includes(selectedTrip.status) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => syncTripTasks(selectedTrip.tripId)}
                      disabled={syncingTasks === selectedTrip.tripId}
                      className="text-emerald-600 border-emerald-200 hover:bg-emerald-50 hover:border-emerald-300 px-2 py-1 h-8 w-8"
                      title="Sync trip tasks"
                    >
                      <RefreshCw className={`w-4 h-4 ${syncingTasks === selectedTrip.tripId ? "animate-spin" : ""}`} />
                    </Button>
                  )}
                </div>
              </div>

              {/* Shipments Content */}
              {loadingShipments ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : tripShipments.length === 0 ? (
                <div className="text-center py-8 bg-slate-50 rounded-lg">
                  <Package className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                  <p className="text-slate-500">No shipments synced yet</p>
                  <p className="text-sm text-slate-400">Click "Sync" to fetch shipments</p>
                </div>
              ) : viewMode === 'list' ? (
                /* List View */
                <div className="border rounded-lg overflow-hidden overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-center px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap w-14">Priority</th>
                        <th className="text-center px-3 py-2 text-xs font-semibold uppercase tracking-wider text-emerald-600 whitespace-nowrap w-14">
                          <span className="flex items-center justify-center gap-1">
                            <Route className="w-3 h-3" /> Edg
                          </span>
                        </th>
                        <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Status</th>
                        <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Type</th>
                        <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Shipment ID</th>
                        <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Package ID</th>
                        <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Reference ID</th>
                        <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">LBN</th>
                        <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Pincode</th>
                        <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Address</th>
                        <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Contact</th>
                        <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Payment</th>
                        <th className="text-right px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Value</th>
                        <th className="text-center px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Map</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getSortedShipments().map((shipment) => {
                        const mapLink = getMapLink(shipment);
                        const relevantAddr = getRelevantAddress(shipment);
                        const fullAddress = formatAddress(relevantAddr);
                        const contactNumber = relevantAddr?.contactNumber || shipment?.recipientMobileNo;
                        return (
                          <tr key={shipment._id} className="border-t hover:bg-slate-50">
                            <td className="px-3 py-2 align-middle text-center">
                              <Badge variant="outline" className="text-xs font-medium">
                                {shipment.priority ?? "—"}
                              </Badge>
                            </td>
                            <td className="px-3 py-2 align-middle text-center">
                              {shipment.sequenceByEdg != null ? (
                                <Badge className="bg-emerald-100 text-emerald-700 text-xs font-bold">
                                  {shipment.sequenceByEdg}
                                </Badge>
                              ) : (
                                <span className="text-slate-300">—</span>
                              )}
                            </td>
                            <td className="px-3 py-2 align-middle">
                              <Badge className={`${getShipmentStatusColor(shipment.bolt_status || shipment.status)} text-xs`}>
                                {(shipment.bolt_status || shipment.status)?.replace(/_/g, " ") || "—"}
                              </Badge>
                            </td>
                            <td className="px-3 py-2 align-middle">
                              <Badge className={`text-xs ${shipment.type === "RETURN" ? "bg-amber-100 text-amber-800" : "bg-blue-100 text-blue-800"}`}>
                                {shipment.type || "—"}
                              </Badge>
                            </td>
                            <td className="px-3 py-2 align-middle">
                              <div className="flex items-center gap-1.5">
                                <button
                                  onClick={() => window.open(`/pharmeasy/orders/${shipment._id}`, '_blank')}
                                  className="font-mono text-sm text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                                >
                                  {shipment.shipmentId}
                                  <ExternalLink className="w-3 h-3" />
                                </button>
                                <button 
                                  onClick={(e) => { e.stopPropagation(); copyToClipboard(shipment.shipmentId, 'Shipment ID'); }} 
                                  className="text-slate-400 hover:text-blue-600 hover:bg-blue-50 p-1 rounded"
                                  title="Copy Shipment ID"
                                >
                                  <Copy className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </td>
                            <td className="px-3 py-2 align-middle">
                              {shipment.packageId ? (
                                <div className="flex items-center gap-1.5">
                                  <button
                                    onClick={() => window.open(`/pharmeasy/orders/${shipment._id}`, '_blank')}
                                    className="text-sm text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                                  >
                                    {shipment.packageId}
                                    <ExternalLink className="w-3 h-3" />
                                  </button>
                                  <button 
                                    onClick={(e) => { e.stopPropagation(); copyToClipboard(shipment.packageId, 'Package ID'); }} 
                                    className="text-slate-400 hover:text-blue-600 hover:bg-blue-50 p-1 rounded"
                                    title="Copy Package ID"
                                  >
                                    <Copy className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              ) : (
                                <span className="text-sm text-slate-400">—</span>
                              )}
                            </td>
                            <td className="px-3 py-2 align-middle">
                              {shipment.referenceId ? (
                                <div className="flex items-center gap-1.5">
                                  <span className="font-mono text-sm text-slate-600 truncate max-w-[120px]">{shipment.referenceId}</span>
                                  <button 
                                    onClick={(e) => { e.stopPropagation(); copyToClipboard(shipment.referenceId, 'Reference ID'); }} 
                                    className="text-slate-400 hover:text-blue-600 hover:bg-blue-50 p-1 rounded flex-shrink-0"
                                    title="Copy Reference ID"
                                  >
                                    <Copy className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              ) : (
                                <span className="text-sm text-slate-400">—</span>
                              )}
                            </td>
                            <td className="px-3 py-2 align-middle">
                              {shipment.lbn ? (
                                <div className="flex items-center gap-1.5">
                                  <span className="font-mono text-sm text-slate-600">{shipment.lbn}</span>
                                  <button 
                                    onClick={(e) => { e.stopPropagation(); copyToClipboard(shipment.lbn, 'LBN'); }} 
                                    className="text-slate-400 hover:text-blue-600 hover:bg-blue-50 p-1 rounded"
                                    title="Copy LBN"
                                  >
                                    <Copy className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              ) : (
                                <span className="text-sm text-slate-400">—</span>
                              )}
                            </td>
                            <td className="px-3 py-2 align-middle">
                              <span className="text-sm text-slate-600">{relevantAddr?.pincode || shipment.dropPincode || shipment.pincode || "—"}</span>
                            </td>
                            <td className="px-3 py-2 align-middle max-w-[180px] group relative">
                              <div className="flex items-center gap-1.5">
                                <span className="text-sm text-slate-500 truncate block cursor-help" title={fullAddress}>
                                  {fullAddress}
                                </span>
                                {fullAddress && fullAddress !== "—" && (
                                  <button 
                                    onClick={(e) => { e.stopPropagation(); copyToClipboard(fullAddress, 'Address'); }} 
                                    className="text-slate-400 hover:text-blue-600 hover:bg-blue-50 p-1 rounded flex-shrink-0"
                                    title="Copy Address"
                                  >
                                    <Copy className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </div>
                              {/* Tooltip on hover */}
                              {fullAddress && fullAddress !== "—" && fullAddress.length > 30 && (
                                <div className="absolute z-50 hidden group-hover:block bg-slate-800 text-white text-xs rounded-lg p-2 max-w-[300px] left-0 top-full mt-1 shadow-lg whitespace-normal">
                                  {fullAddress}
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-2 align-middle">
                              {contactNumber ? (
                                <div className="flex items-center gap-1.5">
                                  <a href={`tel:${contactNumber}`} className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1">
                                    <Phone className="w-3 h-3" />
                                    {contactNumber}
                                  </a>
                                  <button 
                                    onClick={(e) => { e.stopPropagation(); copyToClipboard(contactNumber, 'Contact'); }} 
                                    className="text-slate-400 hover:text-blue-600 hover:bg-blue-50 p-1 rounded"
                                    title="Copy Contact"
                                  >
                                    <Copy className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              ) : (
                                <span className="text-sm text-slate-400">—</span>
                              )}
                            </td>
                            <td className="px-3 py-2 align-middle">
                              <Badge variant="outline" className={`text-xs ${
                                shipment.paymentType === "COD" ? "text-amber-600 border-amber-300" : "text-green-600 border-green-300"
                              }`}>
                                {shipment.paymentType}
                              </Badge>
                            </td>
                            <td className="px-3 py-2 align-middle text-right">
                              <span className="text-sm font-medium">
                                {formatCurrency(shipment.shipmentValue)}
                              </span>
                            </td>
                            <td className="px-3 py-2 align-middle text-center">
                              {mapLink ? (
                                <a href={mapLink} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                                  <Navigation className="w-4 h-4 inline" />
                                </a>
                              ) : (
                                <span className="text-slate-300">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                /* Map View */
                <div className="bg-white rounded-lg border overflow-hidden">
                  {(() => {
                    const embedUrl = buildDirectionsEmbedUrl(tripShipments, hubLocation?.coordinates);
                    const fullMapUrl = buildFullMapUrl(tripShipments, hubLocation?.coordinates);
                    const sortedShipments = sortByEdgSequence 
                      ? [...tripShipments].sort((a, b) => (a.sequenceByEdg ?? 999) - (b.sequenceByEdg ?? 999))
                      : tripShipments;
                    
                    if (embedUrl) {
                      return (
                        <div className="relative">
                          {/* Interactive Google Maps iframe */}
                          <iframe
                            src={embedUrl}
                            width="100%"
                            height="450"
                            style={{ border: 0 }}
                            allowFullScreen=""
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            title={`Trip ${selectedTrip.tripId} Route Map`}
                            className="w-full"
                          />
                          
                          {/* Hub/Pickup Info */}
                          <div className="absolute top-4 right-4 bg-emerald-600 text-white px-3 py-2 rounded-lg text-sm font-medium shadow-lg flex items-center gap-2">
                            <Home className="w-4 h-4" />
                            <span>Pickup: {hubLocation?.name || "Hub"}</span>
                          </div>
                          
                          {/* Delivery Sequence panel */}
                          <div className="absolute top-4 left-4 bg-white/95 backdrop-blur rounded-lg p-3 max-h-64 overflow-y-auto shadow-lg">
                            <p className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-2">
                              <Truck className="w-4 h-4" /> 
                              Delivery Sequence {sortByEdgSequence ? "(Edg Optimized)" : "(Priority)"}:
                            </p>
                            {sortedShipments.filter(s => getCoordinates(s)).slice(0, 15).map((s, i) => (
                              <div key={s._id} className="text-xs text-slate-600 flex items-center gap-2 py-1">
                                <span className="w-5 h-5 rounded-full bg-blue-500 text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                                  {sortByEdgSequence ? (s.sequenceByEdg || i+1) : (s.priority || i+1)}
                                </span>
                                <span className="truncate max-w-[180px]">
                                  {s.shipmentId} - {s.dropPincode || s.pincode}
                                </span>
                              </div>
                            ))}
                            {sortedShipments.filter(s => getCoordinates(s)).length > 15 && (
                              <p className="text-xs text-slate-400 mt-1">+{sortedShipments.filter(s => getCoordinates(s)).length - 15} more stops</p>
                            )}
                          </div>
                          
                          {/* Open in Google Maps button */}
                          {fullMapUrl && (
                            <a 
                              href={fullMapUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="absolute bottom-4 right-4 bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-emerald-700 flex items-center gap-2 shadow-lg"
                            >
                              <Navigation className="w-4 h-4" /> Open Full Route
                            </a>
                          )}
                        </div>
                      );
                    } else {
                      return (
                        <div className="h-[400px] flex items-center justify-center text-slate-400">
                          <div className="text-center">
                            <MapPin className="w-12 h-12 mx-auto mb-2 opacity-50" />
                            <p>No coordinates available for map view</p>
                            <p className="text-sm mt-2">Click "Optimize" to geocode addresses</p>
                          </div>
                        </div>
                      );
                    }
                  })()}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </PharmeasyLayout>
  );
};

export default PharmeasyTrips;
